import { createAction } from '@reduxjs/toolkit';
import { deleteAuth, getAuth, putAuth } from '../../../services/AuthHttpService';
import { QuickAppType } from '../../../interfaces/quickApp';
import { AppThunk } from '../../store';
import {
  ApiDevPortalAppStatus,
  ApplicationAssetFile,
  ApplicationInterface,
  ApplicationLinks,
  ApplicationManagerInterface,
} from '../../../interfaces/application';
import { DEV_PORTAL_API_URL } from '../../../app-config';

// Actions

export const getAppError = createAction<string>('GET_APP_ERROR');
export const resetApp = createAction('RESET_APP');
export const resetApps = createAction('RESET_APPS');
export const addQuickApp = createAction<QuickAppType>('ADD_QUICK_APP');
export const addApp = createAction<ApplicationInterface>('ADD_APP');
export const editApp = createAction<ApplicationInterface>('EDIT_APP');
export const editAppInfo =
  createAction<{ name: string; value: string | string[] | ApplicationLinks }>('EDIT_INFO');
export const editAppSetup = createAction<{ name: string; value: string | string[] }>('EDIT_SETUP');
export const editAppAssets =
  createAction<{ name: string; value: ApplicationAssetFile | ApplicationAssetFile[] }>(
    'EDIT_ASSETS',
  );
export const editAppRating = createAction<{ name: string; value: string }>('EDIT_RATING');
export const openDeleteDialog = createAction<boolean>('OPEN_DELETE_DIALOG');

// Thunk Actions

export const getAppsAction = createAction<ApplicationInterface[] | undefined>('GET_APPS');
export const getApps = (): AppThunk => async (dispatch) => {
  try {
    const apps = await getAuth<ApplicationInterface[]>(`${DEV_PORTAL_API_URL}/api/developer/apps`);

    return dispatch(getAppsAction(apps.parsedBody));
  } catch (e) {
    return dispatch(getAppError('Error fetching applications.'));
  }
};

export const getAppAction = createAction<ApplicationInterface>('GET_APP');
export const getApp =
  (id: string, manager?: boolean): AppThunk =>
  async (dispatch) => {
    if (manager) {
      try {
        const app = await getAuth<ApplicationManagerInterface>(
          `${DEV_PORTAL_API_URL}/api/manager/apps/${id}`,
        );
        const parsedApp = app.parsedBody?.application;

        return parsedApp
          ? dispatch(getAppAction(parsedApp))
          : dispatch(getAppError('Error parsing manager application.'));
      } catch (e) {
        return dispatch(getAppError('Error fetching manager application.'));
      }
    } else {
      try {
        const app = await getAuth<ApplicationInterface>(
          `${DEV_PORTAL_API_URL}/api/developer/apps/${id}`,
        );
        const parsedApp = app.parsedBody;

        return parsedApp
          ? dispatch(getAppAction(parsedApp))
          : dispatch(getAppError('Error parsing application.'));
      } catch (e) {
        return dispatch(getAppError('Error fetching application.'));
      }
    }
  };

export const getAppStatusAction = createAction<ApiDevPortalAppStatus>('GET_APP_STATUS');
export function getAppStatus(id: string): AppThunk {
  return async (dispatch) => {
    try {
      const app = await getAuth<ApiDevPortalAppStatus>(
        `${DEV_PORTAL_API_URL}/api/developer/apps/${id}/status`,
      );
      const parsedApp = app.parsedBody;

      return parsedApp
        ? dispatch(getAppStatusAction(parsedApp))
        : dispatch(getAppError('Error parsing application status.'));
    } catch (e) {
      return dispatch(getAppError('Error fetching application status.'));
    }
  };
}

export const deleteAppAction = createAction('DELETE_APP');
export function deleteApp(
  appId: string,
  manager?: boolean,
  username?: string,
  cluster?: string,
): AppThunk {
  return async (dispatch) => {
    if (manager) {
      const app = await deleteAuth<ApplicationInterface>(
        `${DEV_PORTAL_API_URL}/api/manager/apps?applicationId=${appId}&username=${username}&cluster=${cluster}`,
      );
      const parsedApp = app.parsedBody;

      return parsedApp
        ? dispatch(deleteAppAction())
        : dispatch(getAppError('Error parsing delete application.'));
    }
    const app = await deleteAuth<ApplicationInterface>(
      `${DEV_PORTAL_API_URL}/api/developer/apps?applicationId=${appId}`,
    );
    const parsedApp = app.parsedBody;

    return parsedApp
      ? dispatch(deleteAppAction())
      : dispatch(getAppError('Error parsing delete application.'));
  };
}

export const submitAppAction = createAction<ApplicationInterface>('SUBMIT_APP');
export function submitApp(appId: string): AppThunk {
  return async (dispatch) => {
    try {
      const app = await getAuth<ApplicationInterface>(
        `${DEV_PORTAL_API_URL}/api/developer/submit-app?applicationId=${appId}`,
      );
      const parsedApp = app.parsedBody;

      return parsedApp
        ? dispatch(submitAppAction(parsedApp))
        : dispatch(getAppError('Error parsing submitted application.'));
    } catch (e) {
      return dispatch(getAppError('Error submitting application.'));
    }
  };
}

export const saveDraftAppAction = createAction('SAVE_DRAFT');
export function saveDraftApp(app: ApplicationInterface): AppThunk {
  return async (dispatch) => {
    try {
      const res = await putAuth<ApplicationInterface>(
        `${DEV_PORTAL_API_URL}/api/developer/apps?applicationId=${app.info.applicationId}`,
        app,
      );

      return res.parsedBody
        ? dispatch(saveDraftAppAction())
        : dispatch(getAppError('Error parsing draft application.'));
    } catch (e) {
      return dispatch(getAppError('Error saving draft application.'));
    }
  };
}
